import { useRef, useState } from 'react';
import { api } from '../lib/api';
import { useStore } from '../state/store';

/**
 * The signed-in producer's own picture, shown against their messages.
 *
 * Same camera-or-file choice as a presenter photo, since it gets taken the same
 * way: on a phone, in a corridor, thirty seconds before it is needed.
 */
export default function ProfilePhoto() {
  const user = useStore(s => s.user)!;
  const setUser = useStore(s => s.setUser);
  const notify = useStore(s => s.notify);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    setBusy(true);
    try {
      const { downscaleToDataUrl } = await import('../lib/image');
      const dataUrl = await downscaleToDataUrl(file, 256);
      const r = await api.setMyPhoto(dataUrl);
      setUser({ ...user, photoUrl: r.url });
      notify('Photo updated.');
      setOpen(false);
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  const initials = user.displayName.trim().split(/\s+/)
    .map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?';

  return (
    <>
      <button className="profile-btn" onClick={() => setOpen(true)}
              title="Your picture" aria-label="Your picture">
        {user.photoUrl
          ? <img className="avatar" src={user.photoUrl} alt="" style={{ width: 28, height: 28 }} />
          : <span className="avatar avatar-glyph"
                  style={{ width: 28, height: 28, fontSize: 12, background: 'var(--accent)' }}>
              {initials}
            </span>}
      </button>

      {open && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setOpen(false)}>
          <div className="modal" style={{ maxWidth: 360 }} role="dialog" aria-label="Your picture">
            <h2>Your picture</h2>
            <p className="sub">Shown against your messages during a show.</p>
            <div className="dropzone" onClick={() => fileRef.current?.click()}>
              {user.photoUrl
                ? <img src={user.photoUrl} alt="Your current picture" />
                : <div>No picture yet</div>}
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn" onClick={() => cameraRef.current?.click()} disabled={busy}>
                Take photo
              </button>
              <button className="btn" onClick={() => fileRef.current?.click()} disabled={busy}>
                Choose file
              </button>
              <div className="header-spacer" />
              <button className="btn" onClick={() => setOpen(false)}>Done</button>
            </div>
            <input ref={cameraRef} type="file" hidden accept="image/*" capture="user"
                   onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
            <input ref={fileRef} type="file" hidden accept="image/jpeg,image/png,image/webp"
                   onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
          </div>
        </div>
      )}
    </>
  );
}
